import React, { useState, useEffect, useRef } from 'react';
import { 
  Eye, 
  Download, 
  Trash2, 
  Video, 
  Image, 
  FileText,
  Upload,
  RefreshCw,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  X
} from 'lucide-react';
import { uploadApi } from '@/services/api';
import { toast } from 'sonner';

// Utility function
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Tamaño desconocido';
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  
  while (size >= 1024 && i < sizes.length - 1) {
    size /= 1024;
    i++;
  }
  
  return `${size.toFixed(1)} ${sizes[i]}`;
};

interface FileItem {
  id: string;
  originalName?: string;
  filename?: string;
  mimetype?: string;
  type?: string;
  size?: number;
  url?: string;
  path?: string;
  course?: {
    title: string;
  };
  createdAt?: string;
  isLesson?: boolean;
}

// Modal Video Viewer Component
const VideoModal: React.FC<{ 
  file: FileItem; 
  isOpen: boolean; 
  onClose: () => void; 
}> = ({ file, isOpen, onClose }) => {
  const modalVideoRef = useRef<HTMLVideoElement>(null);
  const [isModalPlaying, setIsModalPlaying] = useState(false);
  const [isModalMuted, setIsModalMuted] = useState(false);
  const [showModalControls, setShowModalControls] = useState(true);

  const toggleModalPlay = async () => {
    if (!modalVideoRef.current) return;
    
    try {
      if (isModalPlaying) {
        modalVideoRef.current.pause();
        setIsModalPlaying(false);
      } else {
        await modalVideoRef.current.play();
        setIsModalPlaying(true);
      }
    } catch (error) {
      console.error('Error playing modal video:', error);
    }
  };

  const toggleModalMute = () => {
    if (modalVideoRef.current) {
      modalVideoRef.current.muted = !isModalMuted;
      setIsModalMuted(!isModalMuted);
    }
  };

  const enterFullscreen = () => {
    if (modalVideoRef.current) {
      if (modalVideoRef.current.requestFullscreen) {
        modalVideoRef.current.requestFullscreen();
      } else if ((modalVideoRef.current as any).webkitRequestFullscreen) {
        (modalVideoRef.current as any).webkitRequestFullscreen();
      } else if ((modalVideoRef.current as any).mozRequestFullScreen) {
        (modalVideoRef.current as any).mozRequestFullScreen();
      }
    }
  };

  const handleModalVideoEnd = () => {
    setIsModalPlaying(false);
  };

  // Close modal on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset video state when modal opens
  React.useEffect(() => {
    if (isOpen && modalVideoRef.current) {
      modalVideoRef.current.currentTime = 0;
      setIsModalPlaying(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all duration-200"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Video container */}
      <div 
        className="relative max-w-4xl max-h-screen w-full h-full flex items-center justify-center p-4"
        onMouseEnter={() => setShowModalControls(true)}
        onMouseLeave={() => setShowModalControls(false)}
      >
        <video
          ref={modalVideoRef}
          className="max-w-full max-h-full object-contain"
          src={file.url}
          onEnded={handleModalVideoEnd}
          playsInline
          controls={false}
        />

        {/* Video Controls Overlay */}
        <div className={`absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center transition-opacity duration-200 ${showModalControls || !isModalPlaying ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleModalPlay}
              className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-4 transition-all duration-200 shadow-lg"
            >
              {isModalPlaying ? (
                <Pause className="w-8 h-8 text-gray-800" />
              ) : (
                <Play className="w-8 h-8 text-gray-800 ml-1" />
              )}
            </button>
            
            <button
              onClick={toggleModalMute}
              className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-3 transition-all duration-200 shadow-lg"
            >
              {isModalMuted ? (
                <VolumeX className="w-6 h-6 text-gray-800" />
              ) : (
                <Volume2 className="w-6 h-6 text-gray-800" />
              )}
            </button>

            <button
              onClick={enterFullscreen}
              className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-3 transition-all duration-200 shadow-lg"
              title="Pantalla completa"
            >
              <Maximize className="w-6 h-6 text-gray-800" />
            </button>
          </div>
        </div>

        {/* Video Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
          <h3 className="text-white text-lg font-medium">
            {file.originalName || file.filename || 'Video sin nombre'}
          </h3>
          <p className="text-white text-sm opacity-75 mt-1">
            {file.course?.title && `${file.course.title} • `}
            {formatFileSize(file.size)}
          </p>
        </div>
      </div>
    </div>
  );
};

// Video Player Component
const VideoPlayer: React.FC<{
  file: FileItem;
  onExpandClick: () => void;
}> = ({ file, onExpandClick }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay
  const [showControls, setShowControls] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Detect if this is a YouTube video
  const isYouTube = file.mimetype === 'video/youtube' || file.url?.includes('youtube.com') || file.url?.includes('youtu.be');
  const youtubeVideoId = isYouTube && file.url ? extractYouTubeVideoId(file.url) : null;

  function extractYouTubeVideoId(url: string): string | null {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  }

  // Debug logging
  React.useEffect(() => {
    console.log('🎥 VideoPlayer mounted for:', file.url);
    console.log('🎥 File details:', file);
  }, [file]);

  const togglePlay = async () => {
    if (!videoRef.current) {
      console.error('🎥 No video ref available');
      return;
    }
    
    console.log('🎥 Toggle play clicked, current state:', { 
      isPlaying, 
      readyState: videoRef.current.readyState,
      src: videoRef.current.src 
    });
    
    try {
      if (isPlaying) {
        console.log('🎥 Pausing video');
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        console.log('🎥 Attempting to play video');
        // Force unmute if needed for user interaction
        videoRef.current.muted = isMuted;
        
        // Try to play
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
          console.log('🎥 Video playing successfully');
          setIsPlaying(true);
        }
      }
    } catch (error) {
      console.error('🎥 Error playing video:', error);
      const errorObj = error as any;
      console.error('🎥 Video error details:', {
        error: errorObj.name + ': ' + errorObj.message,
        networkState: videoRef.current?.networkState,
        readyState: videoRef.current?.readyState,
        src: file.url
      });
      
      if (errorObj.name === 'NotAllowedError') {
        toast.error('Haz clic para permitir reproducción de video');
      } else {
        toast.error('Error al reproducir video: ' + (errorObj.message || 'Error desconocido'));
        setHasError(true);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
  };

  const handleVideoError = (e: any) => {
    console.error('Video error:', e);
    console.error('Video error details:', {
      error: videoRef.current?.error,
      networkState: videoRef.current?.networkState,
      readyState: videoRef.current?.readyState,
      src: file.url
    });
    setHasError(true);
    setIsLoading(false);
    toast.error('Error al cargar video');
  };

  const handleVideoLoad = () => {
    console.log('Video loaded:', file.url);
    setIsLoading(false);
    setHasError(false);
  };

  const handleCanPlay = () => {
    console.log('Video can play:', file.url);
    setIsLoading(false);
  };

  if (hasError) {
    return (
      <div className="w-full h-48 bg-gray-800 rounded-lg flex flex-col items-center justify-center text-white">
        <Video className="w-8 h-8 mb-2 text-gray-400" />
        <p className="text-sm">Error al cargar video</p>
        <p className="text-xs text-gray-400 mt-1">
          {file.originalName || file.filename}
        </p>
        <button 
          onClick={() => {
            setHasError(false);
            setIsLoading(true);
            if (videoRef.current) {
              videoRef.current.load();
            }
          }}
          className="mt-2 px-3 py-1 bg-blue-600 text-xs rounded hover:bg-blue-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  // YouTube video component
  if (isYouTube && youtubeVideoId) {
    return (
      <div className="relative group bg-black rounded-lg overflow-hidden">
        <iframe
          className="w-full h-48 object-cover"
          src={`https://www.youtube.com/embed/${youtubeVideoId}?enablejsapi=1&controls=1&modestbranding=1`}
          title={file.originalName || file.filename || 'Video de YouTube'}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />

        {/* YouTube badge */}
        <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded flex items-center">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
          </svg>
          YouTube
        </div>

        {/* Video Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
          <h3 className="text-white text-sm font-medium truncate">
            {file.originalName || file.filename || 'Video sin nombre'}
          </h3>
          <p className="text-white text-xs opacity-75">
            {file.course?.title && `${file.course.title} • `}
            Video de YouTube
          </p>
        </div>
      </div>
    );
  }

  // Regular video player for local videos
  return (
    <div
      className="relative group bg-black rounded-lg overflow-hidden"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      <video
        ref={videoRef}
        className="w-full h-48 object-cover"
        src={file.url}
        onEnded={handleVideoEnd}
        onError={handleVideoError}
        onLoadedData={handleVideoLoad}
        onCanPlay={handleCanPlay}
        preload="metadata"
        playsInline
        controls={false}
        muted
      />

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      )}

      {/* Video Controls Overlay */}
      <div className={`absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center transition-opacity duration-200 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex items-center space-x-2">
          <button
            onClick={togglePlay}
            className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-3 transition-all duration-200 shadow-lg"
            disabled={isLoading}
          >
            {isPlaying ? (
              <Pause className="w-6 h-6 text-gray-800" />
            ) : (
              <Play className="w-6 h-6 text-gray-800 ml-1" />
            )}
          </button>

          <button
            onClick={toggleMute}
            className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 transition-all duration-200 shadow-lg"
            disabled={isLoading}
          >
            {isMuted ? (
              <VolumeX className="w-4 h-4 text-gray-800" />
            ) : (
              <Volume2 className="w-4 h-4 text-gray-800" />
            )}
          </button>

          <button
            onClick={onExpandClick}
            className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 transition-all duration-200 shadow-lg"
            title="Ver en grande"
          >
            <Maximize className="w-4 h-4 text-gray-800" />
          </button>
        </div>
      </div>

      {/* Video Info Overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
        <h3 className="text-white text-sm font-medium truncate">
          {file.originalName || file.filename || 'Video sin nombre'}
        </h3>
        <p className="text-white text-xs opacity-75">
          {file.course?.title && `${file.course.title} • `}
          {formatFileSize(file.size)}
        </p>
      </div>
    </div>
  );
};

// Image Preview Component
const ImagePreview: React.FC<{ file: FileItem }> = ({ file }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <div className="relative bg-gray-100 rounded-lg overflow-hidden">
      {!imageLoaded && !imageError && (
        <div className="w-full h-48 bg-gray-200 animate-pulse flex items-center justify-center">
          <Image className="w-8 h-8 text-gray-400" />
        </div>
      )}
      
      {imageError ? (
        <div className="w-full h-48 bg-gray-200 flex flex-col items-center justify-center">
          <Image className="w-8 h-8 text-gray-400 mb-2" />
          <p className="text-xs text-gray-500">Error al cargar</p>
        </div>
      ) : (
        <img
          src={file.url}
          alt={file.originalName || file.filename || 'Imagen'}
          className={`w-full h-48 object-cover transition-opacity duration-200 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}

      {/* Image Info Overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
        <h3 className="text-white text-sm font-medium truncate">
          {file.originalName || file.filename || 'Imagen sin nombre'}
        </h3>
        <p className="text-white text-xs opacity-75">
          {file.course?.title && `${file.course.title} • `}
          {formatFileSize(file.size)}
        </p>
      </div>
    </div>
  );
};

// Document Preview Component
const DocumentPreview: React.FC<{ file: FileItem }> = ({ file }) => {
  return (
    <div className="bg-white border-2 border-gray-200 rounded-lg p-6 h-48 flex flex-col justify-between hover:border-blue-300 transition-colors duration-200">
      <div className="flex items-center justify-center flex-1">
        <FileText className="w-16 h-16 text-blue-500" />
      </div>
      
      <div>
        <h3 className="text-gray-900 text-sm font-medium truncate mb-1">
          {file.originalName || file.filename || 'Documento sin nombre'}
        </h3>
        <p className="text-gray-500 text-xs">
          {file.course?.title && `${file.course.title} • `}
          {formatFileSize(file.size)}
        </p>
        <p className="text-gray-400 text-xs mt-1">
          {file.mimetype?.split('/')[1]?.toUpperCase() || 'DOCUMENTO'}
        </p>
      </div>
    </div>
  );
};

export const GalleryPage: React.FC = () => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'video' | 'image' | 'document'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [modalVideo, setModalVideo] = useState<FileItem | null>(null);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setIsLoading(true);
      console.log('🔍 Fetching files from API...');
      
      const response = await uploadApi.getGalleryContent();
      console.log('📁 API Response:', response.data);
      
      if (response.data.success) {
        const contentData = response.data.data || [];
        console.log('📂 Content data:', contentData);
        
        // Extract files from content objects (both regular content and lessons)
        const filesData: FileItem[] = [];
        contentData.forEach((content: any) => {
          if (content.files && Array.isArray(content.files)) {
            content.files.forEach((file: any) => {
              filesData.push({
                id: file.id,
                originalName: file.originalName,
                filename: file.fileName,
                mimetype: file.mimeType,
                size: file.size,
                url: file.url,
                path: file.path,
                course: content.course,
                createdAt: file.createdAt || content.createdAt,
                isLesson: content.isLesson || false // Add flag to identify lesson videos
              });
            });
          }
        });
        
        console.log('📁 Extracted files:', filesData);
        setFiles(filesData);
      } else {
        console.warn('⚠️ API response not successful:', response.data);
        setFiles([]);
      }
    } catch (error) {
      console.error('❌ Error fetching files:', error);
      toast.error('Error al cargar archivos');
      setFiles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFile = async (fileId: string, fileName: string) => {
    if (!confirm(`¿Estás seguro de eliminar "${fileName}"?`)) return;
    
    try {
      await uploadApi.deleteFile(fileId);
      toast.success('Archivo eliminado exitosamente');
      await fetchFiles(); // Refrescar lista
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Error al eliminar archivo');
    }
  };

  const getFileIcon = (mimetype?: string) => {
    if (!mimetype) return <FileText className="w-6 h-6 text-gray-500" />;
    
    if (mimetype.startsWith('video/')) return <Video className="w-6 h-6 text-red-500" />;
    if (mimetype.startsWith('image/')) return <Image className="w-6 h-6 text-green-500" />;
    return <FileText className="w-6 h-6 text-blue-500" />;
  };

  const getFileType = (mimetype?: string): 'video' | 'image' | 'document' => {
    if (!mimetype) return 'document';
    if (mimetype.startsWith('video/')) return 'video';
    if (mimetype.startsWith('image/')) return 'image';
    return 'document';
  };


  const filteredFiles = files.filter(file => {
    if (filter === 'all') return true;
    return getFileType(file.mimetype || file.type) === filter;
  });

  const fileTypeCounts = {
    all: files.length,
    video: files.filter(f => getFileType(f.mimetype || f.type) === 'video').length,
    image: files.filter(f => getFileType(f.mimetype || f.type) === 'image').length,
    document: files.filter(f => getFileType(f.mimetype || f.type) === 'document').length,
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Galería de Archivos</h1>
          <p className="text-gray-600">
            Gestiona todos los videos, imágenes y documentos de la plataforma
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchFiles}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{fileTypeCounts.all}</p>
            </div>
            <Upload className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Videos</p>
              <p className="text-2xl font-bold text-red-600">{fileTypeCounts.video}</p>
            </div>
            <Video className="w-8 h-8 text-red-400" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Imágenes</p>
              <p className="text-2xl font-bold text-green-600">{fileTypeCounts.image}</p>
            </div>
            <Image className="w-8 h-8 text-green-400" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Documentos</p>
              <p className="text-2xl font-bold text-blue-600">{fileTypeCounts.document}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-400" />
          </div>
        </div>
      </div>

      {/* Filters and View Controls */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex space-x-2">
          {[
            { key: 'all', label: 'Todos', count: fileTypeCounts.all },
            { key: 'video', label: 'Videos', count: fileTypeCounts.video },
            { key: 'image', label: 'Imágenes', count: fileTypeCounts.image },
            { key: 'document', label: 'Documentos', count: fileTypeCounts.document },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key as any)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>

        {/* View Mode Toggle */}
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'grid'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Galería
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Lista
          </button>
        </div>
      </div>

      {/* Files Display */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando archivos...</p>
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Upload className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {filter === 'all' ? 'No hay archivos subidos' : `No hay ${filter}s subidos`}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        /* Gallery Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredFiles.map((file) => {
            const fileType = getFileType(file.mimetype || file.type);
            
            return (
              <div key={file.id} className="relative group">
                {/* File Preview */}
                {fileType === 'video' ? (
                  <VideoPlayer 
                    file={file} 
                    onExpandClick={() => setModalVideo(file)} 
                  />
                ) : fileType === 'image' ? (
                  <ImagePreview file={file} />
                ) : (
                  <DocumentPreview file={file} />
                )}

                {/* Action Buttons Overlay */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="flex space-x-1">
                    {(file.url || file.path) && (
                      <a 
                        href={file.url || file.path} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all duration-200"
                        title="Ver archivo en nueva pestaña"
                      >
                        <Eye className="w-3 h-3" />
                      </a>
                    )}
                    {(file.url || file.path) && (
                      <a 
                        href={file.url || file.path} 
                        download={file.originalName || file.filename}
                        className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all duration-200"
                        title="Descargar archivo"
                      >
                        <Download className="w-3 h-3" />
                      </a>
                    )}
                    <button 
                      onClick={() => handleDeleteFile(file.id, file.originalName || file.filename || 'archivo')}
                      className="bg-red-500 bg-opacity-80 hover:bg-opacity-100 text-white p-2 rounded-full transition-all duration-200"
                      title="Eliminar archivo"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Additional Info for Documents */}
                {fileType === 'document' && (
                  <div className="mt-2 text-center">
                    <p className="text-xs text-gray-500">
                      Subido: {file.createdAt ? new Date(file.createdAt).toLocaleDateString() : 'Fecha desconocida'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
          {filteredFiles.map((file) => (
            <div key={file.id} className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {getFileIcon(file.mimetype || file.type)}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {file.originalName || file.filename || 'Archivo sin nombre'}
                    </h3>
                    <div className="text-xs text-gray-500 space-y-1">
                      <p>
                        {formatFileSize(file.size)} • 
                        {file.mimetype || file.type || 'Tipo desconocido'}
                      </p>
                      {file.course?.title && (
                        <p>Curso: {file.course.title}</p>
                      )}
                      {file.createdAt && (
                        <p>Subido: {new Date(file.createdAt).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {(file.url || file.path) && (
                    <a 
                      href={file.url || file.path} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md hover:bg-blue-50"
                      title="Ver archivo"
                    >
                      <Eye className="w-4 h-4" />
                    </a>
                  )}
                  {(file.url || file.path) && (
                    <a 
                      href={file.url || file.path} 
                      download={file.originalName || file.filename}
                      className="p-2 text-green-600 hover:text-green-800 border border-green-300 rounded-md hover:bg-green-50"
                      title="Descargar archivo"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                  <button 
                    onClick={() => handleDeleteFile(file.id, file.originalName || file.filename || 'archivo')}
                    className="p-2 text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50"
                    title="Eliminar archivo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video Modal */}
      {modalVideo && (
        <VideoModal 
          file={modalVideo}
          isOpen={!!modalVideo}
          onClose={() => setModalVideo(null)}
        />
      )}
    </div>
  );
};